import React, { useContext, useCallback } from "react";
import { GetServiceDetails_service_service_mls as serviceMls } from "src/api/generated/GetServiceDetails";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { UI, ScreenSize } from "@wwimmo/ui";
import * as Sentry from "@sentry/react";
import { useParams } from "react-router";
import { Route, selectRoute } from "src/config/routes";
import { v4 as uuidv4 } from "uuid";

interface ServiceContentListProps {
    serviceMls: serviceMls[];
}

interface Params {
    serviceid: string;
}

const ServiceContentListBase = (props: ServiceContentListProps) => {
    const { uiStore, navStore } = useContext(RootStoreContext);
    const { serviceid } = useParams<Params>();

    let listIdentifier = `servicecontentlist-${serviceid}`;

    if (!navStore.listMap.has(listIdentifier)) {
        navStore.setListMapValue(listIdentifier, {});
    }

    const listParameters = navStore.getListMapValue(listIdentifier);

    const onRowClick = useCallback(() => {
        navStore.setListMapValue(
            listIdentifier,
            listParameters
                ? { ...listParameters, scrollPosition: window.pageYOffset }
                : { scrollPosition: window.pageYOffset }
        );
    }, [navStore, listParameters, listIdentifier]);

    const renderItem = useCallback(
        (serviceMls: serviceMls, screenSize: ScreenSize) => {
            let listRows: React.ReactElement[] = [];

            const language = serviceMls.language.charAt(0).toUpperCase() + serviceMls.language.slice(1);

            const titleElements = /<h1>(.*?)<\/h1>/g.exec(serviceMls.content);
            const title = titleElements && titleElements.length > 0 ? titleElements[1] : "";

            switch (screenSize) {
                case ScreenSize.DESKTOP:
                    listRows = [
                        <UI.List.Row key={"r-1"}>
                            <UI.List.Cell key={"c-1"} colspan={1} value={title} />
                            <UI.List.Cell key={"c-2"} colspan={1} value={language} />
                        </UI.List.Row>
                    ];
                    break;
                case ScreenSize.MOBILE:
                    listRows = [
                        <UI.List.Row key={"r-1"}>
                            <UI.List.Cell key={"c-1"} colspan={1} value={title} />
                            <UI.List.Cell key={"c-2"} colspan={1} value={language} />
                        </UI.List.Row>
                    ];
                    break;
            }

            const link = `/services/${serviceid}/content/${serviceMls.id}`;

            return (
                <UI.List.Item
                    key={serviceMls.id}
                    screenSize={screenSize}
                    rows={listRows}
                    to={link}
                    onClick={onRowClick}
                />
            );
        },
        [serviceid, onRowClick]
    );

    const renderHeader = useCallback((screenSize: ScreenSize) => {
        let headerRows: React.ReactElement[] = [];

        const title = "Titel";
        const language = "Sprache";

        switch (screenSize) {
            case ScreenSize.DESKTOP:
                headerRows = [
                    <UI.List.Row key={"row-1"}>
                        <UI.List.Cell key={"c-1"} colspan={1} value={title} />
                        <UI.List.Cell key={"c-2"} colspan={1} value={language} />
                    </UI.List.Row>
                ];
                break;

            case ScreenSize.MOBILE:
                headerRows = [
                    <UI.List.Row key={"row-1"}>
                        <UI.List.Cell key={"c-1"} colspan={1} value={title} />
                        <UI.List.Cell key={"c-2"} colspan={1} value={language} />
                    </UI.List.Row>
                ];
                break;
        }

        return <UI.List.Header rows={headerRows} sticky={true} top={130} />;
    }, []);

    return (
        <>
            <div>
                <UI.Button
                    label={"+ Inhalte hinzufügen"}
                    to={selectRoute(Route.serviceContentDetails, null, {
                        serviceid: serviceid,
                        contentid: uuidv4()
                    })}
                    className={"customer-button mt-2 mt-sm-0 mb-3 ml-auto"}
                    onClick={onRowClick}
                />
            </div>
            <UI.List.BasicList
                screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                items={props.serviceMls}
                renderItem={renderItem}
                renderHeader={renderHeader}
                scrollPosition={listParameters?.scrollPosition ?? undefined}
                emptyListMessage="Keine Verbindungen"
            />
        </>
    );
};

export const ServiceContentList = Sentry.withProfiler(observer(ServiceContentListBase));
